import * as React from 'react';
import {
    AcceptIcon,
    AddIcon,
    Button,
    ChevronStartIcon,
    CloseIcon,
    Dialog,
    Flex,
    FlexItem,
    FormLabel,
    Image,
    Loader,
    Text,
    UndoIcon,
    ZoomToFitIcon
} from "@fluentui/react-northstar";
import EditInput from "../Utils/EditInput";
import SharepointPicker from "../Utils/SharepointPicker";
import ImagePicker from "../Utils/ImagePicker";
import ImageCropper from "../Utils/ImageCropper";
import ImagePlaceholder from "../../assets/imagePlaceholder.png";
import FolderNameSelectorDialog from "./FolderNameSelectorDialog";
import {blobsRootFolderName, formatImageLink, uploadBlobFolder, uploadBlobImage} from "../../apis/blobImageApi";
import {ImageGallery} from '../../Interfaces/ImageGallery';
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";

interface IImagePickerModalProps {
    show: boolean
    onClose: () => void,
    images: Array<ImageGallery>,
    onImagesUpdated: (images: Array<ImageGallery>) => Promise<void>,
}

interface IImagePickerModalState {
    image: { name: string, content: string, width: number, height: number, extension: string } | undefined,
    updatingDatabase: boolean,
    errorMessage: string,
    imageName: string,
    imageLocation: string,
    showFolderSelector: boolean,
    showFolderCreator: boolean,
    showImageCropperTool: boolean,
    selectingImage: boolean,
    croppedImageContent: string | undefined,
    forbiddenImagesNames: Array<string>,
    forbiddenFolderNames: Array<string>,
}

/**
 * Default state
 */
const defaultState: IImagePickerModalState = {
    image: undefined,
    updatingDatabase: false,
    errorMessage: "",
    imageName: "",
    imageLocation: "",
    showFolderSelector: false,
    showFolderCreator: false,
    showImageCropperTool: false,
    selectingImage: false,
    croppedImageContent: undefined,
    forbiddenImagesNames: new Array<string>(),
    forbiddenFolderNames: new Array<string>(),
};

/**
 * Image picker modal
 */
class ImagePickerModal extends React.Component<IImagePickerModalProps, IImagePickerModalState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    // Cropper tool
    // @ts-ignore
    private cropperTool = React.createRef<ImageCropper>();

    constructor(props: IImagePickerModalProps) {
        super(props);
        this.state = defaultState;
    }

    /**
     * Component initialization
     */
    componentDidMount() {
        this.resetSelectFolderItems();
    }

    /**
     * Allow component to rerender
     * @param nextProps next props
     */
    shouldComponentUpdate(nextProps: Readonly<IImagePickerModalProps>): boolean {
        return this.props.show || nextProps.show;
    }

    /**
     * Component has updated
     * @param prevProps
     * @param prevState
     */
    componentDidUpdate(prevProps: Readonly<IImagePickerModalProps>, prevState: Readonly<IImagePickerModalState>) {
        // Reset state on close
        if (prevProps.show && !this.props.show) {
            this.setState(defaultState);
            this.resetSelectFolderItems();
        }

        // Update image name when changing image
        if (prevState.image !== this.state.image && !!this.state.image) {
            // Remove extension
            let formatedName = this.state.image.name.substring(0, this.state.image.name.indexOf("."));
            // Remove unwanted chars
            formatedName = formatedName.replace(/[^a-zA-Z0-9-_]/g, "");
            formatedName = formatedName.substring(0, 100);
            this.setState({imageName: formatedName});
        }
    }

    /**
     * Reset list of available folders when selecting location
     */
    resetSelectFolderItems = () => {
        // Set available folders
        this.setState({
            showFolderSelector: false,
            imageLocation: "",
        });
    }

    /**
     * When an image is selected
     * @param name image name
     * @param content image base64 content
     * @param width image width
     * @param height image height
     * @param extension image extension
     */
    handleImageSelection = (name: string, content: string, width: number, height: number, extension: string) => {
        this.setState({
            image: {name, content, width, height, extension},
            croppedImageContent: undefined,
            selectingImage: false
        });
    }

    /**
     * Upload image to database
     */
    uploadImage = async () => {
        if (this.state.image === undefined ||
            !this.state.imageName ||
            !this.state.imageLocation) return;

        this.setState({updatingDatabase: true, errorMessage: ""});

        const imageLink = blobsRootFolderName +
            formatImageLink(this.state.imageLocation + "/" + this.state.imageName + "." + this.state.image?.extension);

        // Image content
        const content = this.state.croppedImageContent ?? this.state.image?.content;

        // Image
        const image = {
            imageLink: imageLink,
            content
        } as ImageGallery;

        uploadBlobImage(image)
            .then(async success => {
                if (success) {
                    await this.props.onImagesUpdated([...this.props.images, image]);
                    this.setState({updatingDatabase: false});
                    this.props.onClose();
                } else {
                    console.error("Can't upload image");
                    this.setState({updatingDatabase: false});
                }
            })
    }

    /**
     * Create a new folder
     */
    createFolder = (name: string) => {
        if (!!this.state.imageLocation) {
            this.setState({updatingDatabase: true})
            // Subfolder name
            const subFolderName = this.state.imageLocation +
                (this.state.imageLocation.endsWith("/") ? "" : "/") +
                name.replace(/[^a-zA-Z0-9-_]/g, "");
            // Upload folder
            uploadBlobFolder(subFolderName)
                .then(async success => {
                    if (success) {
                        const newFolder = {imageLink: subFolderName, content: ""} as ImageGallery;
                        await this.props.onImagesUpdated([...this.props.images, newFolder]);
                        this.setState({
                            showFolderCreator: false,
                            updatingDatabase: false,
                            forbiddenFolderNames: [...this.state.forbiddenFolderNames, newFolder.imageLink]
                        });
                    } else {
                        console.error("Can't create folder");
                        this.setState({showFolderCreator: false, updatingDatabase: false});
                    }
                })
        }
    }

    /**
     * Format folder title
     * @param path folder complete path
     * @param onlyName keep only folder name
     */
    formatFolderTitle = (path: string, onlyName: boolean = true) => {
        if (path === blobsRootFolderName) return languageObject.translate("MyImages");
        path = path.replace(blobsRootFolderName, languageObject.translate("MyImages") + "/")
        if (onlyName) path = path.substring(path.lastIndexOf("/") + 1);
        return path;
    }

    /**
     * Crop current image
     */
    cropImage = async () => {
        if (!this.cropperTool.current) {
            console.error("Cropper tool is not loaded");
            return;
        }
        if (!this.state.image) {
            console.error("Can't crop image because undefined");
            return;
        }
        // Retrieve cropped image content
        const content = await this.cropperTool.current.getCroppedImg();
        if (!content) {
            console.error("Cropped image content undefined");
            return;
        }
        this.setState({
            croppedImageContent: content,
            showImageCropperTool: false
        });
    }

    /**
     * Reset image
     */
    resetImage = () => {
        if (!this.cropperTool.current) {
            console.error("Cropper tool is not loaded");
            return;
        }
        this.cropperTool.current.resetState();
        if (!this.state.croppedImageContent) return;
        this.setState({croppedImageContent: undefined});
    }

    /**
     * Handle folder selection
     */
    handleFolderSelection = (folder: string, items: Array<string>) => {
        this.setState({
            imageLocation: folder,
            forbiddenImagesNames: items.filter(item => item.indexOf(".") > -1),
            forbiddenFolderNames: items.filter(item => item.indexOf(".") === -1),
        })
    }

    /**
     * Is image already existing
     */
    isImageExisting = () => {
        return this.state.forbiddenImagesNames
            .includes(this.state.imageLocation + this.state.imageName + "." + this.state.image?.extension);
    }

    /**
     * Render component
     */
    render() {
        // Disable confirm button
        const isConfirmBtnDisabled =
            this.state.image === undefined ||
            !this.state.imageName ||
            !this.state.imageLocation ||
            this.state.updatingDatabase ||
            this.isImageExisting();

        // Footer text
        const footerText = !this.state.imageLocation ?
            languageObject.translate("PleaseSelectFolder")
            :
            <>
                {languageObject.translate("YouHaveSelectedFolder") + " "}
                <Text weight={"semibold"} color={"brand"}>
                    {this.state.imageLocation === blobsRootFolderName ?
                        languageObject.translate("MyImages") : this.formatFolderTitle(this.state.imageLocation)}
                </Text>
            </>

        return (
            <Dialog
                open={this.props.show}
                content={
                    <Flex fill column styles={{minHeight: "420px"}}>
                        {this.renderMainContent(!this.state.showFolderSelector)}
                        {this.renderFolderSelector(this.state.showFolderSelector)}
                        {this.renderFolderNameSelector()}
                        {this.renderImageCropperTool()}
                    </Flex>
                }
                header={languageObject.translate("UploadImage")}
                headerAction={{
                    icon: <CloseIcon outline/>,
                    title: 'Close',
                    onClick: this.props.onClose,
                }}
                footer={
                    this.state.showFolderSelector ?
                        <Flex vAlign={"center"}>
                            <Text weight={"semibold"} content={footerText} truncated/>
                            <FlexItem push>
                                <Button primary
                                        loading={this.state.updatingDatabase}
                                        disabled={!this.state.imageLocation || this.state.updatingDatabase}
                                        content={languageObject.translate("Select")}
                                        icon={<AcceptIcon outline/>}
                                        onClick={() => this.setState({showFolderSelector: false})}/>
                            </FlexItem>
                        </Flex>
                        :
                        <FlexItem push>
                            <Button primary
                                    loading={this.state.updatingDatabase}
                                    disabled={isConfirmBtnDisabled}
                                    icon={<AddIcon outline/>}
                                    content={languageObject.translate("Add")}
                                    onClick={this.uploadImage}/>
                        </FlexItem>
                }
            />
        );
    }

    /**
     * Render dialog main content
     */
    renderMainContent = (show: boolean) => {
        // Title for select folder button
        const selectFolderBtnTitle = !this.state.imageLocation ?
            languageObject.translate("SelectFolder") : this.formatFolderTitle(this.state.imageLocation, false);

        return (
            <Flex fill column styles={{display: show ? "flex" : "none"}}>
                {/* Image picker */}
                <ImagePicker
                    onCrop={() => this.setState({showImageCropperTool: true})}
                    onLoading={() => this.setState({selectingImage: true})}
                    handleFileSelection={this.handleImageSelection}/>

                {/* Image preview */}
                <Flex id={"picture_viewer_modal"} hAlign={"center"} vAlign={"center"}>
                    {this.state.selectingImage ?
                        <Loader/>
                        :
                        <Image id="picture_modal" src={!!this.state.image ?
                            this.state.croppedImageContent ?? this.state.image.content
                            :
                            ImagePlaceholder}/>
                    }
                </Flex>

                {/* Image location */}
                <FormLabel className={"form-input"}
                           content={<Text>{languageObject.translate("Location")}<Text color={"red"}> *</Text></Text>}/>
                <Button fluid onClick={() => this.setState({showFolderSelector: true})}
                        disabled={!this.state.image}
                        content={selectFolderBtnTitle} tinted={!!this.state.imageLocation}/>
                {!!this.state.errorMessage &&
                    <Text align={"center"} color={"red"} content={this.state.errorMessage}
                          styles={{marginTop: "10px", marginBottom: "5px"}}/>
                }

                {/* Image name input */}
                <EditInput
                    required
                    disabled={!this.state.imageLocation}
                    edit={!!this.state.imageLocation}
                    max={100}
                    label={languageObject.translate("ImageName")}
                    errorMessage={this.isImageExisting() ? languageObject.translate("ImageAlreadyExistingWithThisName") : ""}
                    value={this.state.imageName ?? ""}
                    setValue={value => (value.length === 0 || value.match(/^[a-zA-Z0-9-_ ]+$/)) ? this.setState({imageName: value}) : null}
                />

            </Flex>
        )
    }

    /**
     * Render folder selector
     */
    renderFolderSelector = (show: boolean) => {
        // Disable create new folder button
        const isCreateFolderBtnDisabled = !this.state.imageLocation ||
            this.state.updatingDatabase;

        return (
            <Flex fill column styles={{display: show ? "flex" : "none"}}>
                <Flex vAlign={"center"} gap={"gap.medium"} styles={{marginBottom: "15px"}}>
                    {/* Return back button */}
                    <Button secondary
                            styles={{width: "30px"}}
                            icon={<ChevronStartIcon outline/>}
                            iconOnly
                            className={"form-input"}
                            onClick={this.resetSelectFolderItems}/>
                    {/* Add subfolder button */}
                    <FlexItem push>
                        <Button loading={this.state.updatingDatabase}
                                disabled={isCreateFolderBtnDisabled}
                                icon={<AddIcon outline/>}
                                className={"form-input"}
                                content={languageObject.translate("NewSubFolder")}
                                onClick={() => this.setState({showFolderCreator: true})}/>
                    </FlexItem>
                </Flex>
                <SharepointPicker
                    onlyFolders
                    styles={{height: "300px"}}
                    items={[blobsRootFolderName, ...this.props.images.map(img => img.imageLink)]}
                    handleSelect={this.handleFolderSelection}
                    noItemsContent={
                        <Flex fill column id={"sharepoint_picker"} hAlign={"center"} vAlign={"center"}>
                            <Text content={languageObject.translate("NoFolder")}/>
                        </Flex>
                    }
                />
            </Flex>
        )
    }

    /**
     * Render folder name selector dialog
     */
    renderFolderNameSelector = () => {
        return (
            <FolderNameSelectorDialog
                loading={this.state.updatingDatabase}
                show={this.state.showFolderCreator}
                onCreateFolder={this.createFolder}
                currentFolder={this.state.imageLocation}
                onHide={() => this.setState({showFolderCreator: false})}
                existingFolders={this.state.forbiddenFolderNames}
            />
        )
    }

    /**
     * Render image cropper tool
     */
    renderImageCropperTool = () => {
        return !!this.state.image && (
            <Dialog
                open={this.state.showImageCropperTool}
                confirmButton={languageObject.translate("Confirm")}
                onConfirm={this.cropImage}
                cancelButton={languageObject.translate("Cancel")}
                onCancel={() => this.setState({showImageCropperTool: false})}
                content={
                    <ImageCropper
                        // @ts-ignore
                        innerRef={this.cropperTool}
                        imageSrc={this.state.croppedImageContent ?? this.state.image.content}
                        imageWidth={this.state.image.width}
                        imageHeight={this.state.image.height}
                    />
                }
                footer={
                    <Flex gap={"gap.small"}>
                        <Button className={"delete_button"} icon={<UndoIcon outline/>}
                                content={languageObject.translate("Reset")}
                                onClick={this.resetImage}/>
                        <FlexItem push>
                            <Button icon={<CloseIcon outline/>} content={languageObject.translate("Cancel")}
                                    onClick={() => this.setState({showImageCropperTool: false})}/>
                        </FlexItem>
                        <Button icon={<ZoomToFitIcon outline/>} primary content={languageObject.translate("Crop")}
                                onClick={this.cropImage}/>
                    </Flex>
                }
                header={languageObject.translate("CropImage")}
                headerAction={{
                    icon: <CloseIcon outline/>,
                    title: 'Close',
                    onClick: () => this.setState({showImageCropperTool: false}),
                }}/>
        )
    }
}

export default ImagePickerModal;